import { Text, XStack, YStack } from 'tamagui'

import { useCurrentLesson } from '@hooks/useCurrentLesson'

import ActivityPage from '@components/Activity/ActivityPage'
import { HeroIcon } from '@components/HeroIcon'
import { MediaRender } from '@components/MediaRender'
import { useMediaSize } from '@hooks/useMediaSize'
import { useLessonTabStore } from '@stores/useLessonTabStore'
import { NotSupported } from '../extra_contents/NotSupported'
import { CourseContentNavigation } from './CourseContentNavigation'
import { MediaRenderWrapper } from './MediaRenderWrapper'
import { TabsContentContainer } from './TabsContentContainer'
import { useTinyPlayer } from '@hooks/useTinyPlayer'
import { GestureResponderEvent, useWindowDimensions } from 'react-native'
import { useScrollControl } from '@hooks/useScrollControl'

export interface IScreenProps {
  hasPreviousContent: boolean
  hasNextContent: boolean
  isCollapsibleOpen?: boolean
  toggleCollapsible?: () => void
}

export function MobileScreen({
  hasPreviousContent,
  hasNextContent,
}: IScreenProps) {
  const { setIsScrollingContent } = useScrollControl()
  const { currentTab, setCurrentTab } = useLessonTabStore()
  const { isTinyPlayer } = useTinyPlayer()

  const { height } = useWindowDimensions()

  const mediaSize = useMediaSize()
  const { currentLesson } = useCurrentLesson()
  const hasDescription = Boolean(currentLesson?.description)
  const hasExtraContent = Boolean((currentLesson?.extraMedias?.length ?? 0) > 0)

  const lessonWithoutContent = !hasDescription && !hasExtraContent
  const paddingBottom = lessonWithoutContent ? height * 0.125 : 0

  const extraMedia = currentLesson?.media ?? null

  const isActivity = !!(
    currentLesson?.activity?.examQuestionsCount &&
    currentLesson?.activity?.examQuestionsCount > 0
  )

  const handlePress = (event: GestureResponderEvent) => {
    event.stopPropagation()
    setIsScrollingContent(false)
  }

  return (
    <YStack w="100%" onPress={handlePress} role="button">
      <XStack
        gap="$spacing-inset-sm"
        $gtSm={{
          mt: '$spacing-inset-lg',
          marginHorizontal: 48,
        }}
        h="100%"
      >
        <YStack flex={1} w="100%">
          <YStack
            backgroundColor="$neutral-0"
            p="$spacing-inset-xs"
            gap="$spacing-inset-xs"
            paddingBottom={
              lessonWithoutContent && !isTinyPlayer
                ? '$spacing-inset-xxl'
                : '$spacing-inset-xs'
            }
            maxHeight={!isActivity ? 750 : 'unset'}
            $gtSm={{
              borderTopLeftRadius: '$border-radius-md',
              borderTopRightRadius: '$border-radius-md',
              p: '$spacing-inset-sm',
            }}
          >
            <XStack
              gap="$spacing-inset-xs"
              w="100%"
              alignItems="center"
              justifyContent="space-between"
            >
              {mediaSize === 'sm' && (
                <XStack w="100%">
                  <CourseContentNavigation
                    visibilityType="top"
                    onPreviousPress={() => {}}
                    onNextPress={() => {}}
                    hasPreviousContent={hasPreviousContent}
                    hasNextContent={hasNextContent}
                  />
                </XStack>
              )}
            </XStack>

            {/* Player Controls */}

            {!isActivity && extraMedia && (
              <XStack alignItems="center" justifyContent="center" w="100%">
                <MediaRenderWrapper media={extraMedia}>
                  <MediaRender
                    media={extraMedia}
                    isImageVariant={extraMedia.type === 'Image'}
                    thumbnailUrl={currentLesson?.coverImage?.cdnFileUrl}
                  >
                    <NotSupported media={extraMedia} />
                  </MediaRender>
                </MediaRenderWrapper>
              </XStack>
            )}
            {isActivity && <ActivityPage />}
            {/* Content General Information */}
            {!isActivity && (
              <YStack
                gap="$spacing-inset-sm"
                paddingHorizontal="$spacing-inset-xxxs"
              >
                <YStack gap="$spacing-inset-sm">
                  <Text
                    color="$neutral-800"
                    fontSize="$font-size-lg"
                    fontFamily="$mulishBold"
                    fontWeight="700"
                    numberOfLines={2}
                  >
                    {currentLesson?.title || ''}
                  </Text>

                  <XStack alignItems="center" justifyContent="space-between">
                    <YStack
                      gap="$spacing-inset-quarck"
                      alignItems="center"
                      p="$spacing-inset-nano"
                    >
                      <HeroIcon
                        icon={['fas', 'check-circle']}
                        size={21}
                        color="$success-300"
                      />
                      <Text
                        color="$neutral-800"
                        fontSize="$font-size-sm"
                        fontFamily="$mulishRegular"
                        lineHeight={24}
                      >
                        Concluído
                      </Text>
                    </YStack>

                    {mediaSize === 'xs' && (
                      <CourseContentNavigation
                        onPreviousPress={() => {}}
                        onNextPress={() => {}}
                        hasPreviousContent={hasPreviousContent}
                        hasNextContent={hasNextContent}
                      />
                    )}
                  </XStack>
                </YStack>
              </YStack>
            )}
          </YStack>
          <YStack paddingBottom={paddingBottom}>
            <TabsContentContainer
              currentLesson={currentLesson}
              currentTab={currentTab}
              onTabChange={setCurrentTab}
            />
          </YStack>
        </YStack>
      </XStack>
    </YStack>
  )
}
